<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">Deep Link Config</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2" style="margin-top: 1rem">
        <v-card-title class="py-4 px-4">
          <v-row align="center" no-gutters>
            <v-col cols="12" sm="12" md="3" class="d-flex align-center pb-3 pb-md-0">
              <v-icon color="blue-grey darken-2 mr-5" size="28">mdi-link-variant</v-icon>
              <div>
                <h3 class="text-h6 mb-1">Deep Link Configuration</h3>
                <p class="text-caption text-grey-darken-1 mb-0">Manage deep links</p>
              </div>
            </v-col>
            <v-col cols="12" sm="12" md="9">
              <v-row align="center" justify="end" no-gutters>
                <v-col cols="12" sm="5" md="5">
                  <v-text-field
                    v-model="search"
                    prepend-inner-icon="mdi-magnify"
                    placeholder="Search by name or URL"
                    hide-details
                    variant="outlined"
                    rounded
                    class="search-field"
                    clearable
                  />
                </v-col>
                <v-col v-if="permissions.add" cols="auto" class="ml-2 ml-sm-3">
                  <v-btn color="primary" dark class="btn-layout text-none" @click="openDialog()">
                    <v-icon left>mdi-plus</v-icon>
                    Add Deep Link
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-item>
          <div class="table-container">
            <v-data-table-server
              v-model:options="options"
              :headers="headers"
              :items="deepLinks"
              :items-length="totalDeepLinks"
              :loading="loading"
              item-key="id"
              class="elevation-1 my-data-table modern-table"
              density="comfortable"
              :items-per-page="options.itemsPerPage"
              fixed-header
            >
              <template #item.linkType="{ item }">
                <v-chip
                  :color="item.linkType === 'Callback' ? 'orange' :
                   item.linkType === 'WebsiteLink' ? 'secondary' :
                   'primary'"
                  size="small"
                  variant="tonal"
                >
                  {{ 
                    (item.linkType || '').toLowerCase() === 'callback' ? 'Callback' :
                    (item.linkType || '').toLowerCase() === 'websitelink' ? 'Website Link' :
                   'Deep Link' }}
                </v-chip>
              </template>
              <template #item.deepLinkURL="{ item }">
                <a :href="item.deepLinkURL" class="deep-link-url" target="_blank" rel="noopener">
                  {{ item.deepLinkURL }}
                </a>
              </template>
              <template #item.updateDate="{ item }">
                {{ formatDate(item.updateDate || item.createDate) }}
              </template>
              <template #item.actions="{ item }">
                <div class="action-group">
                  <v-btn variant="text" density="comfortable" @click="openDialog(item)" class="action-btn edit-btn" icon :disabled="!item.activeFlag">
                    <v-icon size="18">{{ permissions.edit ? 'mdi-pencil-outline' : 'mdi-eye-outline' }}</v-icon>
                    <v-tooltip activator="parent" location="top">{{ permissions.edit ? 'Edit' : 'View' }}</v-tooltip>
                  </v-btn>
                  <v-btn v-if="permissions.delete" variant="text" density="comfortable" @click="confirmDelete(item)" class="action-btn delete-btn" icon :disabled="!item.activeFlag">
                    <v-icon size="18">mdi-delete-outline</v-icon>
                    <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                  </v-btn>
                </div>
              </template>
              <template #no-data>
                <div class="text-center py-6 text-medium-emphasis">No deep links found</div>
              </template>
            </v-data-table-server>
          </div>
        </v-card-item>
      </v-card>
    </v-container>

    <v-dialog v-model="dialog" max-width="520px" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="primary">mdi-link-variant</v-icon>
          <span class="text-h6 font-weight-bold">{{ editMode ? (permissions.edit ? 'Edit Deep Link' : 'View Deep Link') : 'Add Deep Link' }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-6">
          <v-form ref="form" v-model="formValid">
            <v-text-field
              v-model="formData.name"
              label="Link name"
              prepend-inner-icon="mdi-tag-text-outline"
              variant="outlined"
              density="comfortable"
              :rules="[rules.required]"
              hide-details="auto"
            />
            <v-select
              v-model="formData.linkType"
              label="Link type"
              prepend-inner-icon="mdi-format-list-bulleted-type"
              variant="outlined"
              density="comfortable"
              :items="linkTypeOptions"
              item-title="text"
              item-value="value"
              :rules="[rules.required]"
              hide-details="auto"
              class="mt-4"
            />
            <v-text-field
              v-model="formData.deepLinkURL"
              :label="formData.linkType === 'Callback' ? 'Callback Text' : 'Deep Link URL'"
              :prepend-inner-icon="formData.linkType === 'Callback' ? 'mdi-text' : 'mdi-link'"
              :placeholder="formData.linkType === 'Callback' ? 'Enter callback text' : 'e.g., myapp://path'"
              variant="outlined"
              density="comfortable"
              :rules="[rules.required, rules.urlOrText]"
              hide-details="auto"
              class="mt-4"
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-spacer />
          <v-btn variant="text" class="text-none" @click="closeDialog">{{ isViewOnly ? 'Close' : 'Cancel' }}</v-btn>
          <v-btn v-if="editMode ? permissions.edit : permissions.add" color="primary" class="text-none" :loading="saving" @click="saveDeepLink">
            {{ editMode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="420px">
      <v-card>
        <v-card-title class="font-weight-bold">Remove deep link</v-card-title>
        <v-card-text>
          Are you sure you want to delete
          <strong>{{ selectedItem?.name }}</strong>?
          This will mark the deep link as inactive.
        </v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-spacer />
          <v-btn variant="text" class="text-none" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn v-if="permissions.delete" color="error" class="text-none" :loading="deleteLoading" @click="deleteDeepLink">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'DeepLinkConfig',
  inject: ['flagNav', 'FontColorTitle', 'SubNavbarBackGround'],
  data() {
    const todayDate = new Date();
    const dd = String(todayDate.getDate()).padStart(2, '0');
    const mm = String(todayDate.getMonth() + 1).padStart(2, '0');
    const yyyy = todayDate.getFullYear();

    return {
      today: `${dd}/${mm}/${yyyy}`,
      loading: false,
      saving: false,
      deleteLoading: false,
      dialog: false,
      deleteDialog: false,
      formValid: false,
      showInactive: false,
      search: '',
      searchDebounceTimer: null,
      deepLinks: [],
      totalDeepLinks: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      selectedItem: null,
      formData: {
        id: null,
        name: '',
        deepLinkURL: '',
        linkType: 'DeepLink',
      },
      linkTypeOptions: [
        { text: 'Deep Link', value: 'DeepLink' },
        { text: 'Callback', value: 'Callback' },
        { text: 'Website Link', value: 'WebsiteLink' },
      ],
      headers: [
        { title: 'Name', key: 'name', sortable: true },
        { title: 'Type', key: 'linkType', sortable: true, width: '120px' },
        { title: 'Deep Link', key: 'deepLinkURL', sortable: true },
        { title: 'Updated', key: 'updateDate', sortable: true },
        { title: 'Actions', key: 'actions', sortable: false, width: '120px' },
      ],
      rules: {
        required: (v) => !!v || 'Required',
        urlOrText: (v) => {
          if (!v) return 'Required';
          // Only validate URL format for DeepLink type
          if (this.formData.linkType === 'DeepLink') {
            const pattern = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;
            return pattern.test(v) || 'Provide a valid URL or deep link (must include scheme)';
          }
          // For Callback, accept any text
          return true;
        },
      },
    };
  },
  computed: {
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('DeepLinkConfig');
    },
    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('DeepLinkConfig'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('DeepLinkConfig'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('DeepLinkConfig'));
    },
    permissions() {
      return {
        add: this.$pagePermission && this.$pagePermission.canAdd('DeepLinkConfig'),
        edit: this.$pagePermission && this.$pagePermission.canEdit('DeepLinkConfig'),
        delete: this.$pagePermission && this.$pagePermission.canDelete('DeepLinkConfig'),
      }
    },
    editMode() {
      return !!this.formData.id;
    },
  },
  watch: {
    options: {
      handler() {
        this.loadDeepLinks();
      },
      deep: true,
      immediate: false,
    },
    search: {
      handler() {
        this.handleSearchInput();
      },
    },
    showInactive() {
      this.options.page = 1;
      this.loadDeepLinks();
    },
    'formData.linkType'() {
      // Re-validate the URL field when linkType changes
      this.$nextTick(() => {
        this.$refs.form?.validate();
      });
    },
  },
  mounted() {
    this.loadDeepLinks();
  },
  beforeUnmount() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
  methods: {
    loadDeepLinks() {
      this.loading = true;
      const { page, itemsPerPage, sortBy } = this.options;
      let sortField = null;
      let sortOrder = null;

      if (sortBy && sortBy.length > 0) {
        const sortOption = sortBy[0];
        sortField = typeof sortOption === 'string' ? sortOption : sortOption?.key;
        if (typeof sortOption === 'object' && sortOption?.order) {
          sortOrder = sortOption.order === 'desc' ? 'desc' : 'asc';
        } else {
          sortOrder = 'asc';
        }
      }

      const payload = {
        searchText: this.search?.trim() || '',
        page: page || 1,
        itemsPerPage: itemsPerPage || 10,
        sortBy: sortField || null,
        sortOrder: sortOrder || null,
        includeInactive: this.showInactive || false,
      };

      this.$call.SearchDeepLinks(
        payload,
        (dataResponse) => {
          if (dataResponse.status === 0) {
            const responseData = dataResponse.data || {};
            this.totalDeepLinks = responseData.totalCount ?? 0;
            this.deepLinks =
              (responseData.items || []).map((item) => ({
                id: item.id ?? item.ID,
                name: item.name ?? item.Name,
                deepLinkURL: item.deepLinkURL ?? item.DeepLinkURL ?? null,
                linkType: item.linkType ?? item.LinkType ?? 'DeepLink',
                activeFlag: item.activeFlag ?? item.ActiveFlag ?? true,
                createDate: item.createDate ?? item.CreateDate,
                createBy: item.createBy ?? item.CreateBy,
                updateDate: item.updateDate ?? item.UpdateDate,
                updateBy: item.updateBy ?? item.UpdateBy,
              })) || [];
          } else {
            this.deepLinks = [];
            this.totalDeepLinks = 0;
            this.$swal.fire('Error', dataResponse.message || 'Failed to load deep links', 'error');
          }
          this.loading = false;
        },
        (errorResource) => {
          console.error('SearchDeepLinks error:', errorResource);
          this.loading = false;
          this.deepLinks = [];
          this.totalDeepLinks = 0;
          this.$swal.fire('Error', 'Failed to load deep links', 'error');
        }
      );
    },
    handleSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.options.page = 1;
        this.loadDeepLinks();
      }, 400);
    },
    async saveDeepLink() {
      const validation = await this.$refs.form.validate();
      if (!validation.valid) {
        return;
      }
      this.saving = true;
      const payload = {
        id: this.formData.id,
        name: this.formData.name?.trim(),
        deepLinkURL: this.formData.deepLinkURL?.trim(),
        linkType: this.formData.linkType,
      };
      if (this.editMode) {
        this.$call.UpdateDeepLink(
          payload,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.$swal.fire('Success', 'Deep link updated', 'success');
              this.dialog = false;
              this.loadDeepLinks();
            } else {
              this.$swal.fire('Error', dataResponse.message || 'Failed to update deep link', 'error');
            }
            this.saving = false;
          },
          (errorResource) => {
            console.error('UpdateDeepLink error:', errorResource);
            this.$swal.fire('Error', 'Failed to update deep link', 'error');
            this.saving = false;
          }
        );
      } else {
        this.$call.CreateDeepLink(
          payload,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.$swal.fire('Success', 'Deep link created', 'success');
              this.dialog = false;
              this.loadDeepLinks();
            } else {
              this.$swal.fire('Error', dataResponse.message || 'Failed to create deep link', 'error');
            }
            this.saving = false;
          },
          (errorResource) => {
            console.error('CreateDeepLink error:', errorResource);
            this.$swal.fire('Error', 'Failed to create deep link', 'error');
            this.saving = false;
          }
        );
      }
    },
    openDialog(item = null) {
      this.dialog = true;
      this.$nextTick(() => {
        this.$refs.form?.resetValidation();
      });

      if (item) {
        this.formData = {
          id: item.id,
          name: item.name,
          deepLinkURL: item.deepLinkURL,
          linkType: item.linkType || 'DeepLink',
        };
      } else {
        this.resetForm();
      }
    },
    resetForm() {
      this.formData = {
        id: null,
        name: '',
        deepLinkURL: '',
        linkType: 'DeepLink',
      };
    },
    confirmDelete(item) {
      this.selectedItem = item;
      this.deleteDialog = true;
    },
    deleteDeepLink() {
      if (!this.selectedItem) return;
      this.deleteLoading = true;

      this.$call.DeleteDeepLink(
        this.selectedItem.id,
        (dataResponse) => {
          if (dataResponse.status === 0) {
            this.$swal.fire('Success', 'Deep link removed', 'success');
            this.loadDeepLinks();
          } else {
            this.$swal.fire('Error', dataResponse.message || 'Failed to remove deep link', 'error');
          }
          this.deleteLoading = false;
          this.deleteDialog = false;
        },
        (errorResource) => {
          console.error('DeleteDeepLink error:', errorResource);
          this.$swal.fire('Error', 'Failed to remove deep link', 'error');
          this.deleteLoading = false;
          this.deleteDialog = false;
        }
      );
    },
    formatDate(date) {
      if (!date) return '-';
      const parsed = new Date(date);
      if (isNaN(parsed.getTime())) return '-';
      
      const dd = String(parsed.getDate()).padStart(2, '0');
      const mm = String(parsed.getMonth() + 1).padStart(2, '0');
      const yyyy = parsed.getFullYear();
      const hh = String(parsed.getHours()).padStart(2, '0');
      const min = String(parsed.getMinutes()).padStart(2, '0');
      
      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    },
    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },
  },
};
</script>

<style scoped>
.card2 {
  border-radius: 10px !important;
  margin-bottom: 2rem;
  padding-top: 1rem;
}

.btn-layout {
  padding: 14px 24px;
  min-width: 160px;
  height: 56px;
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
  border-radius: 16px;
}

.search-field .v-input__control .v-input__slot {
  min-height: 48px !important;
  background: #f5f5f5 !important;
  box-shadow: none !important;
  border: 1px solid #e0e0e0 !important;
}

.search-field .v-input__prepend-inner {
  margin-right: 8px;
  margin-left: 4px;
}

.search-field .v-icon {
  color: #666;
}

.table-container {
  overflow-x: auto;
  max-width: 100%;
  position: relative;
}

:deep(.v-table__wrapper thead tr),
:deep(.v-table__wrapper thead th) {
  background-color: #f5f5f5 !important;
}

:deep(.v-data-table) {
  border: 1px solid #e1e1e1;
  border-radius: 16px;
  overflow: hidden;
}

.action-group {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
}

.action-btn {
  border-radius: 12px;
}

.deep-link-url {
  color: #1a73e8;
  text-decoration: none;
  word-break: break-all;
}

.deep-link-url:hover {
  text-decoration: underline;
}

.modern-table :deep(.v-table__wrapper) {
  max-height: 600px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper thead th) {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface, 255, 255, 255));
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar) {
  width: 6px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-track) {
  background: rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb) {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 3px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb:hover) {
  background: rgba(25, 118, 210, 0.5);
}
</style>
